import axios from 'axios';
import history from '../historyData';
import { TENANT_ID, TOKEN } from './constants';
import {
  getIsRefreshing,
  getPendingRefreshPromise,
  isTokenExpired,
  refreshTokens,
} from './tokenManager';

const api = axios.create({
  baseURL: process.env.REACT_APP_REST_SERVER_URL,
});

// Request interceptor - auto-attach auth headers and proactively refresh expired tokens
api.interceptors.request.use(async (config) => {
  // If a token refresh is in progress, wait for it to complete before sending
  if (getIsRefreshing() && getPendingRefreshPromise()) {
    await getPendingRefreshPromise().catch(() => {});
  }

  // eslint-disable-next-line no-undef
  let userToken = localStorage.getItem(TOKEN);
  const tokenExpired = userToken ? isTokenExpired(userToken) : false;

  // Proactively refresh if token is expired before sending the request
  if (userToken && tokenExpired) {
    try {
      userToken = await refreshTokens();
    } catch {
      // Refresh failed, continue with expired token (response interceptor will handle it)
    }
  }

  // eslint-disable-next-line no-undef
  const tenantId = localStorage.getItem(TENANT_ID);

  // eslint-disable-next-line no-param-reassign
  config.headers.Authorization = userToken ? `Bearer ${userToken}` : '';
  if (tenantId) {
    // eslint-disable-next-line no-param-reassign
    config.headers['x-tenant-id'] = tenantId;
  }

  return config;
});

// Response interceptor - handle auth errors matching React Native api.js pattern
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const statusCode = error?.response?.status;
    const apiErrorMessage =
      error?.response?.data?.error?.message || error?.response?.data?.message;

    // Determine if this is a refreshable auth error:
    // - 405 with 'jwt expired' message (access token expired)
    // - 401 (unauthorized - access token may be expired)
    // Non-refreshable 405 errors (session expired, password reset) go straight to logout
    const isJwtExpired = apiErrorMessage === 'jwt expired';
    const isRefreshable =
      (isJwtExpired || statusCode === 401) &&
      originalRequest &&
      !originalRequest?.retried;

    if (isRefreshable) {
      // eslint-disable-next-line no-param-reassign
      originalRequest.retried = true;
      // eslint-disable-next-line no-undef
      const existingToken = localStorage.getItem(TOKEN);

      if (existingToken) {
        try {
          const newToken = await refreshTokens();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch {
          // Refresh failed (refresh token likely expired - server returns 401)
          history.replace('/logout', {
            // eslint-disable-next-line no-undef
            pathname: window?.location?.pathname,
          });
          return Promise.reject(error);
        }
      }
    }

    // Handle non-refreshable 405 errors (session expired, password reset, etc.) - logout directly
    if (statusCode === 405 && !isJwtExpired) {
      history.replace('/logout', {
        // eslint-disable-next-line no-undef
        pathname: window?.location?.pathname,
      });
    }

    return Promise.reject(error);
  },
);

export default api;
